import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full bg-[#050B14]/70 border border-white/15 rounded-xl px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/40 transition-all";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "Poolbau-Projekt", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte füllen Sie Name, E-Mail und Nachricht aus.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        type: "anfrage",
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        message: `[${form.topic}] ${form.message}`,
      });
      setSent(true);
      toast.success("Ihre Anfrage wurde übermittelt.");
    } catch {
      toast.error("Übermittlung fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="relative py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.32em] font-mono text-cyan-400 font-semibold">Kontakt</p>
          <h2 className="mt-4 font-bold tracking-tight leading-tight text-2xl sm:text-4xl lg:text-5xl text-white">
            Lassen Sie uns über <span className="text-cyan-400">Ihren Pool</span> sprechen.
          </h2>
          <p className="mt-5 text-slate-400 text-sm sm:text-base leading-relaxed max-w-md">
            Ob Neubau, Modernisierung oder smarte Poolsteuerung — erzählen Sie uns von Ihrem Projekt.
            Wir antworten persönlich, nicht per Bot.
          </p>
          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center shrink-0">
                <Phone className="w-4.5 h-4.5 w-[18px] h-[18px] text-cyan-300" />
              </span>
              <div>
                <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">Telefon</p>
                <p className="text-sm text-slate-200 mt-0.5">Mo–Fr 8–17 Uhr · Notdienst für Kunden 24/7</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center shrink-0">
                <Mail className="w-[18px] h-[18px] text-cyan-300" />
              </span>
              <div>
                <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">E-Mail</p>
                <p className="text-sm text-slate-200 mt-0.5">info@cool-pool-company.de</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center shrink-0">
                <MapPin className="w-[18px] h-[18px] text-cyan-300" />
              </span>
              <div>
                <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">Einsatzgebiet</p>
                <p className="text-sm text-slate-200 mt-0.5">Ganz Deutschland · Projekte ab 40.000 €</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="pool-inquiry-contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="glass-card rounded-2xl p-7 sm:p-9 bg-[#0A1526]/70 space-y-4"
        >
          {sent ? (
            <div data-testid="inquiry-success-panel" className="py-14 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-teal-400/15 border border-teal-400/40 flex items-center justify-center">
                <Send className="w-6 h-6 text-teal-300" />
              </div>
              <h3 className="mt-5 font-display font-bold text-xl text-white">Anfrage gesendet!</h3>
              <p className="mt-2 text-sm text-slate-400">Wir melden uns innerhalb von 24 Stunden bei Ihnen.</p>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="inquiry-name-input" value={form.name} onChange={set("name")} placeholder="Ihr Name *" className={inputCls} />
                <input data-testid="inquiry-email-input" value={form.email} onChange={set("email")} type="email" placeholder="E-Mail-Adresse *" className={inputCls} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="inquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="Telefon (optional)" className={inputCls} />
                <select data-testid="inquiry-topic-select" value={form.topic} onChange={set("topic")} className={inputCls}>
                  <option>Poolbau-Projekt</option>
                  <option>Poolsteuerung / Technik</option>
                  <option>Modernisierung</option>
                  <option>Wartung &amp; Pflege</option>
                  <option>Sonstiges</option>
                </select>
              </div>
              <textarea
                data-testid="inquiry-message-textarea"
                value={form.message}
                onChange={set("message")}
                rows={5}
                placeholder="Erzählen Sie uns von Ihrem Projekt — Gartengröße, Wünsche, Zeithorizont … *"
                className={`${inputCls} resize-none`}
              />
              <button
                data-testid="inquiry-form-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full cyan-glow-btn text-[#050B14] font-semibold py-4 rounded-full inline-flex items-center justify-center gap-2 text-sm disabled:opacity-60"
              >
                {submitting ? "Wird gesendet…" : (<>Anfrage absenden <Send className="w-4 h-4" /></>)}
              </button>
              <p className="text-[11px] text-slate-500 text-center">
                Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten gemäß Datenschutzerklärung zu.
              </p>
            </>
          )}
        </motion.form>
      </div>
    </section>
  );
}
